import React, { useState } from 'react';
import './KnightsGenerator.css';

const KnightsGenerator = () => {
  const [roleSet, setRoleSet] = useState('KTS');
  const [statementTypes, setStatementTypes] = useState({
    simple: true,
    negations: false,
    and: false,
    ifThen: false
  });
  const [uniqueSolution, setUniqueSolution] = useState(true);
  const [generatedPuzzle, setGeneratedPuzzle] = useState(null);
  const [seed, setSeed] = useState(Math.floor(Math.random() * 10000));

  const PEOPLE = ['A', 'B', 'C'];
  const ROLE_NAMES = { K: 'Knight', T: 'Thief', S: 'Spy' };
  
  const ROLE_SETS = {
    'KTS': { label: 'K/T/S (Knight, Thief, Spy)', roles: ['K', 'T', 'S'] },
    'KKT': { label: 'K/K/T (Knight, Knight, Thief)', roles: ['K', 'K', 'T'] },
    'KTT': { label: 'K/T/T (Knight, Thief, Thief)', roles: ['K', 'T', 'T'] },
    'KSS': { label: 'K/S/S (Knight, Spy, Spy)', roles: ['K', 'S', 'S'] }
  };

  // Utility function to get random element from array
  const randomChoice = (arr) => arr[Math.floor(Math.random() * arr.length)];

  // Generate all possible worlds for given role array
  const generateWorlds = (roleArray) => {
    const worlds = [];
    
    // Generate all permutations of the roles
    const permutations = [];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        for (let k = 0; k < 3; k++) {
          if (i !== j && j !== k && i !== k) {
            permutations.push([roleArray[i], roleArray[j], roleArray[k]]);
          }
        }
      }
    }
    
    // Remove duplicates
    const unique = [...new Set(permutations.map(p => JSON.stringify(p)))].map(p => JSON.parse(p));
    
    return unique.map(perm => ({
      A: perm[0],
      B: perm[1],
      C: perm[2]
    }));
  };

  // Evaluate a simple statement in a world
  const evaluateSimple = (subject, role, negated, world) => {
    const actual = world[subject];
    const result = (actual === role);
    return negated ? !result : result;
  };

  // Generate a simple statement that matches desired truth value
  const generateSimpleStatement = (world, desiredTruth, availableRoles) => {
    const subject = randomChoice(PEOPLE);
    const actual = world[subject];
    const otherRoles = availableRoles.filter(r => r !== actual);
    
    let role, negated;
    
    if (desiredTruth) {
      if (otherRoles.length > 0 && Math.random() < 0.5) {
        // "X is not Y" where Y is not X's actual role (true)
        role = randomChoice(otherRoles);
        negated = true;
      } else {
        // "X is Y" where Y is X's actual role (true)
        role = actual;
        negated = false;
      }
    } else {
      if (otherRoles.length > 0 && Math.random() < 0.5) {
        // "X is Y" where Y is not X's actual role (false)
        role = randomChoice(otherRoles);
        negated = false;
      } else {
        // "X is not Y" where Y is X's actual role (false)
        role = actual;
        negated = true;
      }
    }
    
    return { subject, role, negated, type: 'simple' };
  };

  // Generate AND statement
  const generateAndStatement = (world, desiredTruth, availableRoles) => {
    let left, right;
    
    if (desiredTruth) {
      left = generateSimpleStatement(world, true, availableRoles);
      right = generateSimpleStatement(world, true, availableRoles);
    } else {
      left = generateSimpleStatement(world, false, availableRoles);
      right = generateSimpleStatement(world, Math.random() < 0.5, availableRoles);
    }
    
    return { left, right, type: 'and' };
  };

  // Generate IF-THEN statement
  const generateIfThenStatement = (world, desiredTruth, availableRoles) => {
    let antecedent, consequent;
    
    if (desiredTruth) {
      if (Math.random() < 0.5) {
        antecedent = generateSimpleStatement(world, false, availableRoles);
        consequent = generateSimpleStatement(world, Math.random() < 0.5, availableRoles);
      } else {
        antecedent = generateSimpleStatement(world, Math.random() < 0.5, availableRoles);
        consequent = generateSimpleStatement(world, true, availableRoles);
      }
    } else {
      antecedent = generateSimpleStatement(world, true, availableRoles);
      consequent = generateSimpleStatement(world, false, availableRoles);
    }
    
    return { antecedent, consequent, type: 'if_then' };
  };

  // Evaluate any statement type in a world
  const evaluateStatement = (statement, world) => {
    switch (statement.type) {
      case 'simple':
        return evaluateSimple(statement.subject, statement.role, statement.negated, world);
      case 'and':
        return evaluateStatement(statement.left, world) && evaluateStatement(statement.right, world);
      case 'if_then':
        return !evaluateStatement(statement.antecedent, world) || evaluateStatement(statement.consequent, world);
      default:
        return false;
    }
  };

  // Convert statement to text
  const statementToText = (statement) => {
    const roleText = (role) => ROLE_NAMES[role].toLowerCase();
    
    switch (statement.type) {
      case 'simple':
        return `${statement.subject} is ${statement.negated ? 'not ' : ''}${roleText(statement.role)}.`;
      case 'and':
        const leftText = statementToText(statement.left).slice(0, -1);
        const rightText = statementToText(statement.right).slice(0, -1);
        return `(${leftText}) AND (${rightText}).`;
      case 'if_then':
        const anteText = statementToText(statement.antecedent).slice(0, -1);
        const consText = statementToText(statement.consequent).slice(0, -1);
        return `IF (${anteText}) THEN (${consText}).`;
      default:
        return '';
    }
  };

  // Generate statement for a role
  const generateStatementForRole = (speaker, world, role, availableRoles, allowedTypes) => {
    const desiredTruth = role === 'K' ? true : role === 'T' ? false : Math.random() < 0.5;
    
    const possibleTypes = [];
    if (allowedTypes.simple) possibleTypes.push('simple');
    if (allowedTypes.negations) possibleTypes.push('simple'); // Negations are part of simple
    if (allowedTypes.and) possibleTypes.push('and');
    if (allowedTypes.ifThen) possibleTypes.push('if_then');
    
    if (possibleTypes.length === 0) possibleTypes.push('simple');
    
    const chosenType = randomChoice(possibleTypes);
    
    switch (chosenType) {
      case 'simple':
        return generateSimpleStatement(world, desiredTruth, availableRoles);
      case 'and':
        return generateAndStatement(world, desiredTruth, availableRoles);
      case 'if_then':
        return generateIfThenStatement(world, desiredTruth, availableRoles);
      default:
        return generateSimpleStatement(world, desiredTruth, availableRoles);
    }
  };

  // Check consistency of statements with world
  const isWorldConsistent = (world, statements) => {
    for (const [person, statement] of Object.entries(statements)) {
      const role = world[person];
      const statementValue = evaluateStatement(statement, world);
      
      if (role === 'K' && !statementValue) return false; // Knight must tell truth
      if (role === 'T' && statementValue) return false;  // Thief must lie
      // Spy can say anything
    }
    return true;
  };

  // Calculate difficulty score
  const calculateDifficulty = (consistentWorlds, totalWorlds, statements) => {
    const W_total = totalWorlds;
    const W_ok = consistentWorlds.length;
    
    if (W_total <= 1 || W_ok <= 1) return 0;
    
    const A = Math.log2(W_ok) / Math.log2(W_total);
    
    // Statement complexity
    const getComplexity = (stmt) => {
      switch (stmt.type) {
        case 'simple': return stmt.negated ? 1.1 : 1.0;
        case 'and': return (getComplexity(stmt.left) + getComplexity(stmt.right)) / 2 + 0.5;
        case 'if_then': return (getComplexity(stmt.antecedent) + getComplexity(stmt.consequent)) / 2 + 1.1;
        default: return 1.0;
      }
    };
    
    const complexities = Object.values(statements).map(getComplexity);
    const avgComplexity = complexities.reduce((a, b) => a + b, 0) / complexities.length;
    const C = Math.max(0, Math.min(1, (avgComplexity - 1.0) / (2.2 - 1.0)));
    
    // Simple proof depth calculation
    const D = W_ok === 1 ? 1.0 : 0.5;
    
    return Math.round(10 * (0.55 * A + 0.25 * C + 0.20 * D) * 100) / 100;
  };

  // Generate puzzle
  const generatePuzzle = () => {
    // Simple seeded random number generator
    let currentSeed = seed;
    const seededRandom = () => {
      const x = Math.sin(currentSeed++) * 10000;
      return x - Math.floor(x);
    };
    
    // Override randomChoice to use seeded random
    const seededRandomChoice = (arr) => arr[Math.floor(seededRandom() * arr.length)];
    
    const currentRoles = ROLE_SETS[roleSet].roles;
    const availableRoles = [...new Set(currentRoles)];
    const allWorlds = generateWorlds(currentRoles);
    
    // Update helper functions to use seeded random
    const seededGenerateSimpleStatement = (world, desiredTruth, availableRoles) => {
      const subject = seededRandomChoice(PEOPLE);
      const actual = world[subject];
      const otherRoles = availableRoles.filter(r => r !== actual);
      
      let role, negated;
      
      if (desiredTruth) {
        if (otherRoles.length > 0 && seededRandom() < 0.5) {
          role = seededRandomChoice(otherRoles);
          negated = true;
        } else {
          role = actual;
          negated = false;
        }
      } else {
        if (otherRoles.length > 0 && seededRandom() < 0.5) {
          role = seededRandomChoice(otherRoles);
          negated = false;
        } else {
          role = actual;
          negated = true;
        }
      }
      
      return { subject, role, negated, type: 'simple' };
    };
    
    const seededGenerateAndStatement = (world, desiredTruth, availableRoles) => {
      let left, right;
      
      if (desiredTruth) {
        left = seededGenerateSimpleStatement(world, true, availableRoles);
        right = seededGenerateSimpleStatement(world, true, availableRoles);
      } else {
        left = seededGenerateSimpleStatement(world, false, availableRoles);
        right = seededGenerateSimpleStatement(world, seededRandom() < 0.5, availableRoles);
      }
      
      return { left, right, type: 'and' };
    };
    
    const seededGenerateIfThenStatement = (world, desiredTruth, availableRoles) => {
      let antecedent, consequent;
      
      if (desiredTruth) {
        if (seededRandom() < 0.5) {
          antecedent = seededGenerateSimpleStatement(world, false, availableRoles);
          consequent = seededGenerateSimpleStatement(world, seededRandom() < 0.5, availableRoles);
        } else {
          antecedent = seededGenerateSimpleStatement(world, seededRandom() < 0.5, availableRoles);
          consequent = seededGenerateSimpleStatement(world, true, availableRoles);
        }
      } else {
        antecedent = seededGenerateSimpleStatement(world, true, availableRoles);
        consequent = seededGenerateSimpleStatement(world, false, availableRoles);
      }
      
      return { antecedent, consequent, type: 'if_then' };
    };
    
    const seededGenerateStatementForRole = (speaker, world, role, availableRoles, allowedTypes) => {
      const desiredTruth = role === 'K' ? true : role === 'T' ? false : seededRandom() < 0.5;
      
      const possibleTypes = [];
      if (allowedTypes.simple) possibleTypes.push('simple');
      if (allowedTypes.negations) possibleTypes.push('simple');
      if (allowedTypes.and) possibleTypes.push('and');
      if (allowedTypes.ifThen) possibleTypes.push('if_then');
      
      if (possibleTypes.length === 0) possibleTypes.push('simple');
      
      const chosenType = seededRandomChoice(possibleTypes);
      
      switch (chosenType) {
        case 'simple':
          return seededGenerateSimpleStatement(world, desiredTruth, availableRoles);
        case 'and':
          return seededGenerateAndStatement(world, desiredTruth, availableRoles);
        case 'if_then':
          return seededGenerateIfThenStatement(world, desiredTruth, availableRoles);
        default:
          return seededGenerateSimpleStatement(world, desiredTruth, availableRoles);
      }
    };
    
    for (let attempt = 0; attempt < 1000; attempt++) {
      const targetWorld = seededRandomChoice(allWorlds);
      const statements = {};
      
      for (const person of PEOPLE) {
        const personRole = targetWorld[person];
        statements[person] = seededGenerateStatementForRole(
          person, 
          targetWorld, 
          personRole, 
          availableRoles, 
          statementTypes
        );
      }
      
      const consistentWorlds = allWorlds.filter(world => 
        isWorldConsistent(world, statements)
      );
      
      if (uniqueSolution && consistentWorlds.length === 1) {
        const difficulty = calculateDifficulty(consistentWorlds, allWorlds.length, statements);
        setGeneratedPuzzle({
          statements,
          consistentWorlds,
          difficulty,
          seed,
          targetWorld
        });
        break;
      } else if (!uniqueSolution && consistentWorlds.length >= 1) {
        const difficulty = calculateDifficulty(consistentWorlds, allWorlds.length, statements);
        setGeneratedPuzzle({
          statements,
          consistentWorlds,
          difficulty,
          seed,
          targetWorld
        });
        break;
      }
    }
  };

  const generateWithNewSeed = () => {
    setSeed(Math.floor(Math.random() * 10000));
    // Use setTimeout to ensure state update happens first
    setTimeout(() => generatePuzzle(), 0);
  };

  const handleStatementTypeChange = (type) => {
    setStatementTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  return (
    <div className="knights-generator">
      <div className="generator-controls">
        <div className="control-section">
          <h3>Role Combination</h3>
          <div className="role-set-selection">
            {Object.entries(ROLE_SETS).map(([key, roleSetData]) => (
              <label key={key} className="role-set-option">
                <input 
                  type="radio" 
                  name="roleSet"
                  value={key}
                  checked={roleSet === key}
                  onChange={(e) => setRoleSet(e.target.value)}
                  className="knights-radio"
                />
                {roleSetData.label}
              </label>
            ))}
          </div>
        </div>

        <div className="control-section">
          <h3>Statement Types</h3>
          <div className="statement-types">
            <label>
              <input 
                type="checkbox" 
                className="knights-checkbox"
                checked={statementTypes.simple}
                onChange={() => handleStatementTypeChange('simple')}
              />
              Simple statements
            </label>
            <label>
              <input 
                type="checkbox" 
                className="knights-checkbox"
                checked={statementTypes.negations}
                onChange={() => handleStatementTypeChange('negations')}
              />
              Negations
            </label>
            <label>
              <input 
                type="checkbox" 
                className="knights-checkbox"
                checked={statementTypes.and}
                onChange={() => handleStatementTypeChange('and')}
              />
              AND
            </label>
            <label>
              <input 
                type="checkbox" 
                className="knights-checkbox"
                checked={statementTypes.ifThen}
                onChange={() => handleStatementTypeChange('ifThen')}
              />
              IF THEN
            </label>
          </div>
        </div>

        <div className="control-section">
          <h3>Solution Requirements</h3>
          <label>
            <input 
              type="checkbox" 
              className="knights-checkbox"
              checked={uniqueSolution}
              onChange={(e) => setUniqueSolution(e.target.checked)}
            />
            Must have unique solution
          </label>
        </div>

        <div className="control-section">
          <h3>Random Seed</h3>
          <input 
            type="number" 
            value={seed}
            onChange={(e) => setSeed(parseInt(e.target.value) || 0)}
            className="seed-input"
          />
        </div>

        <div className="generate-buttons">
          <button onClick={generatePuzzle} className="generate-button">
            Generate
          </button>
          <button onClick={generateWithNewSeed} className="generate-button secondary">
            New seed and generate
          </button>
        </div>
      </div>

      {generatedPuzzle && (
        <div className="generated-puzzle">
          <div className="puzzle-header">
            <h3>Generated Puzzle</h3>
            <div className="puzzle-meta">
              <span>Difficulty: {generatedPuzzle.difficulty}</span>
              <span>Seed: {generatedPuzzle.seed}</span>
            </div>
          </div>
          
          <div className="statements">
            {PEOPLE.map(person => (
              <div key={person} className="statement">
                <strong>{person} says:</strong> {statementToText(generatedPuzzle.statements[person])}
              </div>
            ))}
          </div>
          
          <div className="solutions">
            <h4>Solutions:</h4>
            {generatedPuzzle.consistentWorlds.length === 1 ? (
              <div className="solution unique-solution">
                <strong>Unique solution:</strong> 
                <span className="world">
                  A = {ROLE_NAMES[generatedPuzzle.consistentWorlds[0].A]}, 
                  B = {ROLE_NAMES[generatedPuzzle.consistentWorlds[0].B]}, 
                  C = {ROLE_NAMES[generatedPuzzle.consistentWorlds[0].C]}
                </span>
              </div>
            ) : (
              <div className="multiple-solutions">
                <div className="solutions-count">
                  <strong>{generatedPuzzle.consistentWorlds.length} possible solutions:</strong>
                </div>
                {generatedPuzzle.consistentWorlds.map((world, index) => (
                  <div key={index} className="solution">
                    <span className="solution-number">{index + 1}.</span>
                    <span className="world">
                      A = {ROLE_NAMES[world.A]}, 
                      B = {ROLE_NAMES[world.B]}, 
                      C = {ROLE_NAMES[world.C]}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnightsGenerator;